<template>
    <PageContainer>
        <PageHeader>
            {{ $t('product.create.page_title') }}
        </PageHeader>

        <div class="container-fluid">
            <form
                enctype="application/x-www-form-urlencoded"
                autocomplete="off"
                @submit.prevent="onVerify"
                class="row"
            >
                <!-- name -->
                <div class="col-md-6">
                    <div class="form-group mb-3">
                        <label for="product_name">
                            {{ $t('product.name') }}
                        </label>
                        <input
                            type="text"
                            id="product_name"
                            v-model="form.name"
                            class="form-control"
                            :placeholder="$t('product.name')"
                            :class="[form.errors.name ? 'is-invalid' : '']"
                            minlength="1"
                            maxlength="100"
                        >
                        <form-error v-if="form.errors.name">
                            {{ form.errors.name }}
                        </form-error>
                    </div>
                </div>

                <!-- ref -->
                <div class="col-md-6">
                    <div class="form-group mb-3">
                        <label for="product_ref">
                            {{ $t('product.ref') }}
                        </label>
                        <input
                            type="text"
                            id="product_ref"
                            v-model="form.ref"
                            class="form-control"
                            :class="[form.errors.ref ? 'is-invalid' : '']"
                            :placeholder="$t('product.ref')"
                            minlength="1"
                            maxlength="100"
                        >
                        <form-error v-if="form.errors.ref">
                            {{ form.errors.ref }}
                        </form-error>
                    </div>
                </div>

                <!-- color -->
                <div class="col-md-6">
                    <div class="form-group mb-3">
                        <label for="product_color">
                            {{ $t('product.color') }}
                        </label>
                        <input
                            type="text"
                            id="product_color"
                            v-model="form.color"
                            class="form-control"
                            :class="[form.errors.color ? 'is-invalid' : '']"
                            :placeholder="$t('product.color')"
                            minlength="1"
                            maxlength="100"
                        >
                        <form-error v-if="form.errors.color">
                            {{ form.errors.color }}
                        </form-error>
                    </div>
                </div>

                <!-- productType -->
                <div class="col-md-6">
                    <div class="form-group mb-3">
                        <label for="product_product_types">
                            {{ $t('product.product_type') }}
                        </label>
                        <select
                            id="product_product_types"
                            v-model="form.productType"
                            class="form-select"
                            :class="[form.errors.productType ? 'is-invalid' : '']"
                        >
                            <DefaultOptionElement />
                            <option
                                v-for="productType in productTypes"
                                :key="productType.value"
                                :value="productType.value"
                            >{{ productType.value }}</option>
                        </select>
                        <form-error v-if="form.errors.productType">
                            {{ form.errors.productType }}
                        </form-error>
                    </div>
                </div>

                <!-- gender -->
                <div class="col-md-6">
                    <div class="form-group mb-3">
                        <label for="product_gender">
                            {{ $t('product.gender') }}
                        </label>
                        <select
                            id="product_gender"
                            v-model="form.gender"
                            class="form-select"
                            :class="[form.errors.gender ? 'is-invalid' : '']"
                        >
                            <DefaultOptionElement />
                            <option
                                v-for="gender in genders"
                                :key="gender.value"
                                :value="gender.value"
                            >{{ $t('gender.' + gender.name.toString().toLowerCase()) }}</option>
                        </select>
                        <form-error v-if="form.errors.gender">
                            {{ form.errors.gender }}
                        </form-error>
                    </div>
                </div>

                <!-- permissionId -->
                <div class="col-md-6">
                    <div class="form-group mb-3">
                        <label for="product_permission_id">
                            {{ $t('product.permissionId') }}
                        </label>
                        <input
                            type="text"
                            id="product_permission_id"
                            v-model="form.permissionId"
                            class="form-control"
                            :class="[form.errors.permissionId ? 'is-invalid' : '']"
                            minlength="1"
                            maxlength="5"
                            aria-describedby="product_permission_id_helper"
                            :placeholder="$t('product.permissionId')"
                        >
                        <div class="form-text" id="product_permission_id_helper">
                            {{ $t('product.permissionId.helper')}}
                        </div>
                        <form-error v-if="form.errors.permissionId">
                            {{ form.errors.permissionId }}
                        </form-error>
                    </div>
                </div>

                <!-- predefined quantity -->
                <div class="col-md-6">
                    <div class="form-group mb-3">
                        <label for="product_perdefined_quantity">
                            {{ $t('product.predefinedQuantity') }}
                        </label>
                        <input
                            type="number"
                            id="product_perdefined_quantity"
                            v-model="form.quantity"
                            class="form-control"
                            :class="[form.errors.quantity ? 'is-invalid' : '']"
                            min="1"
                            step="1"
                            aria-describedby="product_perdefined_quantity_helper"
                        >
                        <div class="form-text" id="product_perdefined_quantity_helper">
                            {{ $t('product.predefinedQuantity.helper')}}
                        </div>
                        <form-error v-if="form.errors.quantity">
                            {{ form.errors.quantity }}
                        </form-error>
                    </div>
                </div>

                <!-- sizeType -->
                <div class="col-md-6">
                    <div class="form-group mb-3">
                        <label for="product_size_types">
                            {{ $t('product.size_type') }}
                        </label>
                        <select
                            id="product_size_types"
                            v-model="form.sizeType"
                            class="form-select"
                            :class="[form.errors.sizeType ? 'is-invalid' : '']"
                            @change="form.sizes = null"
                        >
                            <DefaultOptionElement />
                            <option
                                v-for="sizeType in sizeTypes"
                                :key="sizeType"
                                :value="sizeType"
                            >{{ sizeType }}</option>
                        </select>
                        <form-error v-if="form.errors.sizeType">
                            {{ form.errors.sizeType }}
                        </form-error>
                    </div>
                </div>

                <!-- sizes -->
                <div class="col-12">
                    <label for="product_sizes">
                        {{ $t('product.size') }}
                    </label>
                    <div class="row">
                        <div class="col-md-3" v-for="size in getSizes" :key="size">
                            <div class="form-check">
                                <input
                                    type="checkbox"
                                    class="form-check-input"
                                    :class="[form.errors.sizes ? 'is-invalid' : '']"
                                    :value="size"
                                    v-model="form.sizes"
                                    :id="`size-${size}`"
                                >
                                <label :for="`size-${size}`" class="form-check-label">{{size}}</label>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <form-error v-if="form.errors.sizes" class="d-block">
                                {{ form.errors.sizes }}
                            </form-error>
                        </div>
                    </div>
                </div>

                <FormActionContainer class="col-12">
                    <BackButton href="/products" />
                    <SubmitButton :disabled="form.processing" />
                </FormActionContainer>
            </form>
        </div>
    </PageContainer>

    <VerifyModal
        ref="verifyModalRef"
        @process="onProcessForm"
    />
</template>

<script setup lang="ts">
import PageHeader from "@/components/elements/PageHeader.vue";
import PageContainer from "@/components/elements/PageContainer.vue";
import BackButton from "@/components/buttons/BackButton.vue";
import {useForm} from "@inertiajs/vue3";
import FormError from "@/components/elements/FormError.vue";
import SubmitButton from "@/components/buttons/SubmitButton.vue";
import FormActionContainer from "@/components/elements/FormActionContainer.vue";
import DefaultOptionElement from "@/components/elements/DefaultOptionElement.vue";
import {computed, ref} from "vue";
import {useSize} from "@/Composable/useSize.ts";
import VerifyModal from "@/components/elements/VerifyModal.vue";
import type {SelectOption} from "@/components/types.ts";

const {sizesLetters, sizesNumerics, sizeTypes} = defineProps<{
    sizesLetters: Array<string>
    sizesNumerics: Array<string>
    sizeTypes: Array<string>
    productTypes: Array<SelectOption>
    genders: Array<SelectOption>
}>()

const form = useForm({
    name: null,
    ref: "",
    color: "",
    gender: 1,
    sizeType: "Mixed",
    permissionId: null,
    quantity: 1,
    productType: null,
    sizes: []
})

const getSizes = computed(() => {
    const {getSizes} = useSize(sizesNumerics, sizesLetters)
    form.sizes = []
    return getSizes(form.sizeType)
})

const verifyModalRef = ref<InstanceType<typeof VerifyModal> |null>(null)

const onVerify = () => {
    verifyModalRef.value.openModal()
}

const onProcessForm = () => {
    verifyModalRef.value.closeModal();
    form.post('/products')
}

</script>

<style scoped>

</style>
